import * as FileSystem from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import { Platform } from 'react-native';
import * as XLSX from 'xlsx';

/**
 * RFC 4180 CSV text for rows of `{ header: value }`. A value holding a comma, a quote or a line
 * break is quoted, so it reaches the reader exactly as written: callers pass their data through
 * untouched and never rewrite a comma themselves (ATL-1899).
 */
export const buildCsvContent = (data) => {
  const escapeCsvValue = (value) => {
    const strValue = value === null || value === undefined ? '' : String(value);
    if (/[",\r\n]/.test(strValue)) {
      // Double any quotes in the value (CSV escaping standard)
      return `"${strValue.replace(/"/g, '""')}"`;
    }
    return strValue;
  };

  const headers = Object.keys(data[0]).map(escapeCsvValue).join(',');
  const rows = data.map(row => Object.values(row).map(escapeCsvValue).join(','));
  return [headers, ...rows].join('\n');
};

export const exportToCSV = async (data, filename) => {
  const csvContent = buildCsvContent(data);

  if (Platform.OS === 'web') {
    // For web, create a blob and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}.csv`;
    link.click();
  } else {
    // For mobile, save file and share
    const filePath = `${FileSystem.documentDirectory}${filename}.csv`;
    await FileSystem.writeAsStringAsync(filePath, csvContent);
    await Sharing.shareAsync(filePath);
  }
};

export const exportToExcel = async (data, filename) => {
  // Create workbook and worksheet
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(data);
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

  if (Platform.OS === 'web') {
    // For web, use XLSX.writeFile
    XLSX.writeFile(wb, `${filename}.xlsx`);
  } else {
    // For mobile, convert to buffer and save
    const wbout = XLSX.write(wb, { type: 'base64', bookType: 'xlsx' });
    const filePath = `${FileSystem.documentDirectory}${filename}.xlsx`;
    await FileSystem.writeAsStringAsync(filePath, wbout, {
      encoding: FileSystem.EncodingType.Base64,
    });
    await Sharing.shareAsync(filePath);
  }
};

/**
 * Export text content to a text file
 * @param {string} textContent - Text content to export
 * @param {string} filename - Filename without extension
 */
export const exportToTextFile = async (textContent, filename) => {
  if (Platform.OS === 'web') {
    // For web, create a blob and trigger download
    const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}.txt`;
    link.click();
  } else {
    // For mobile, save file and share
    const filePath = `${FileSystem.documentDirectory}${filename}.txt`;
    await FileSystem.writeAsStringAsync(filePath, textContent);
    await Sharing.shareAsync(filePath);
  }
};